import { Navigate, useNavigate } from 'react-router-dom';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAccessibleModules } from '@core/router/useAccessibleModules';
import { ModuleTiles } from '@shared/components/layout/ModuleTiles';
import type { ModuleTile } from '@shared/components/layout/ModuleTiles';

/**
 * Module chooser shown after login when the user has several work modules
 * and no remembered last module. The chosen module is remembered on entry,
 * so this page is normally seen only once.
 */
export const ModuleChooserPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessibleModules } = useAccessibleModules();

  // Opened directly with fewer than two modules - let the landing logic decide
  if (accessibleModules.length < 2) {
    return <Navigate to="/" replace />;
  }

  const tiles: ModuleTile[] = accessibleModules.map((module) => ({
    id: module.id,
    label: t(module.labelKey),
    icon: module.icon,
    isCurrent: false,
    isExternal: false,
    onOpen: () => navigate(module.basePath),
  }));

  return (
    <Stack alignItems="center" sx={{ py: { xs: 2, md: 6 } }}>
      <Card sx={{ maxWidth: 440, width: '100%' }}>
        <CardContent sx={{ p: { xs: 3, md: 4 }, '&:last-child': { pb: { xs: 3, md: 4 } } }}>
          <Stack spacing={0.5} sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h6" component="h1" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {t('app.moduleChooser.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('app.moduleChooser.subtitle')}
            </Typography>
          </Stack>
          <ModuleTiles tiles={tiles} onSelect={(tile) => tile.onOpen()} />
        </CardContent>
      </Card>
    </Stack>
  );
};
